/**
 * Locales y terminales del punto de venta externo (PLAN_POS_IMPLEMENTACION F1).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/lib/apiClient'

export type OversellPolicy = 'block' | 'warn' | 'allow'
export type TerminalStatus = 'pending' | 'active' | 'revoked' | 'inactive'

export interface PosStore {
  id: string
  code: string
  name: string
  address: string
  commune: string
  /** `CdgSIISucur`. `null`: el local es la casa matriz. */
  sii_branch_code: number | null
  warehouse: string
  warehouse_code: string
  warehouse_name: string
  timezone: string
  oversell_policy: OversellPolicy
  tip_suggestion_pct: string | null
  is_active: boolean
  terminals_count: number
}

export interface PosTerminal {
  id: string
  store: string
  store_code: string
  store_name: string
  code: string
  name: string
  mode: 'retail' | 'gastronomia'
  /** `pos`: el punto de venta. `agente`: el agente local que recibe los documentos del sistema de gestión. */
  kind: 'pos' | 'agente'
  enabled_document_types: number[]
  is_active: boolean
  status: TerminalStatus
  last_seen_at: string | null
  app_version: string
  /** El último latido de Clobi Conector (cada minuto). `null` si el equipo nunca latió. */
  heartbeat_at: string | null
  heartbeat: TerminalHeartbeat | Record<string, never>
  activated_at: string | null
  revoked_at: string | null
}

/** Lo que el equipo informó en su último latido (`apps/pos/latido.py`). */
export interface TerminalHeartbeat {
  app_version: string
  config_version: string
  queue: { pending: number | null; failed: number | null; oldest_pending_at: string | null }
  last_error: { at: string | null; message: string } | null
  host: { machine: string; os: string }
  printers: string[]
  /** Reloj del equipo menos el del servidor. `null` si no lo informó. */
  clock_skew_seconds: number | null
}

export interface ActivationCode {
  code: string
  expires_at: string
}

const STORES = ['pos', 'stores']
const TERMINALS = ['pos', 'terminals']

export function usePosStores() {
  return useQuery({
    queryKey: STORES,
    queryFn: async () => (await apiClient.get('/pos/stores/')).data.stores as PosStore[],
  })
}

export function useSavePosStore() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PosStore>) => {
      const res = data.id
        ? await apiClient.patch(`/pos/stores/${data.id}/`, data)
        : await apiClient.post('/pos/stores/', data)
      return res.data as PosStore
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: STORES })
      // El estado de un terminal depende de si su local está activo.
      qc.invalidateQueries({ queryKey: TERMINALS })
    },
  })
}

/** `refetchInterval`: mientras se muestra un código, para ver el «Activo» sin recargar. */
export function usePosTerminals(refetchInterval: number | false = false) {
  return useQuery({
    queryKey: TERMINALS,
    refetchInterval,
    queryFn: async () => (await apiClient.get('/pos/terminals/')).data.terminals as PosTerminal[],
  })
}

export function useSavePosTerminal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PosTerminal>) => {
      const res = data.id
        ? await apiClient.patch(`/pos/terminals/${data.id}/`, data)
        : await apiClient.post('/pos/terminals/', data)
      return res.data as PosTerminal
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TERMINALS })
      qc.invalidateQueries({ queryKey: STORES })
    },
  })
}

export function useActivationCode() {
  return useMutation({
    mutationFn: async (terminalId: string) =>
      (await apiClient.post(`/pos/terminals/${terminalId}/activation-code/`)).data as ActivationCode,
  })
}

export function useRevokeTerminal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (terminalId: string) =>
      (await apiClient.post(`/pos/terminals/${terminalId}/revoke/`)).data as PosTerminal,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: TERMINALS })
      // Revocar obliga a cambiar el PIN a quienes autorizan en ese local.
      qc.invalidateQueries({ queryKey: ['pos', 'operators'] })
    },
  })
}

// ── Folios por terminal (F2) ────────────────────────────────────────────────

export type FolioRangeStatus = 'active' | 'exhausted' | 'revoked'

export interface PosFolioRange {
  id: string
  terminal: string
  terminal_code: string
  store_name: string
  document_type_code: number
  folio_from: number
  folio_to: number
  next_folio: number
  available: number
  status: FolioRangeStatus
  reported_next_folio: number | null
  reported_at: string | null
  caf_valid_from: number
  caf_valid_to: number
  assigned_at: string
  revoked_at: string | null
}

/** Por tipo: desde qué folio saldría el próximo rango y cuántos quedan en ese CAF. */
export interface FolioPool {
  next_folio: number
  caf_valid_to: number | null
  available_in_caf: number
}

export interface PosFolioPolicy {
  document_type_code: number
  buffer_target: number
  alert_threshold: number
  auto_assign: boolean
}

const FOLIOS = ['pos', 'folios']
const POLICIES = ['pos', 'folio-policies']

export function usePosFolios() {
  return useQuery({
    queryKey: FOLIOS,
    queryFn: async () => (await apiClient.get('/pos/folios/')).data as {
      ranges: PosFolioRange[]
      pool: Record<string, FolioPool>
    },
  })
}

export function useAssignFolios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: { terminal: string; document_type_code: number; quantity: number; folio_from?: number }) =>
      (await apiClient.post('/pos/folios/', data)).data as PosFolioRange,
    onSuccess: () => qc.invalidateQueries({ queryKey: FOLIOS }),
  })
}

export function useRevokeFolios() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (rangeId: string) =>
      (await apiClient.post(`/pos/folios/${rangeId}/revoke/`)).data as PosFolioRange,
    onSuccess: () => qc.invalidateQueries({ queryKey: FOLIOS }),
  })
}

export function usePosFolioPolicies() {
  return useQuery({
    queryKey: POLICIES,
    queryFn: async () => (await apiClient.get('/pos/folio-policies/')).data.policies as PosFolioPolicy[],
  })
}

export function useSaveFolioPolicy() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: PosFolioPolicy) =>
      (await apiClient.put('/pos/folio-policies/', data)).data as PosFolioPolicy,
    onSuccess: () => qc.invalidateQueries({ queryKey: POLICIES }),
  })
}

// ── Ventas de los terminales (F3) ───────────────────────────────────────────

export type PosSaleStatus = 'received' | 'signed' | 'sent' | 'accepted' | 'rejected' | 'conflict'

export interface PosSaleProblem {
  field: string
  /** Solo en las notas de crédito: CREDIT_EXCEEDS_BALANCE, LINE_NOT_IN_ORIGINAL… */
  code?: string
  message: string
  expected?: string
  received?: string
}

export interface PosSale {
  id: string
  local_uuid: string
  terminal_code: string
  store_name: string
  operator_id: string
  document_type_code: number
  folio: number
  issue_date: string
  total: string
  status: PosSaleStatus
  ted_check: 'ok' | 'conflict'
  ted_problems: PosSaleProblem[]
  warnings: { code: string; message: string }[]
  document_id: string | null
  document_status: string | null
  payments: { method: string; amount: string; acquirer: string; reference: string }[]
  rejection: { code: string; message: string } | null
  received_at: string
}

export interface PosSalesFilters {
  status?: string
  terminal?: string
  page?: number
}

export function usePosSales(filters: PosSalesFilters) {
  return useQuery({
    queryKey: ['pos', 'sales', filters],
    queryFn: async () => (await apiClient.get('/pos/sales/', { params: filters })).data as {
      count: number
      page: number
      page_size: number
      sales: PosSale[]
      pending: { conflict: number; rejected: number }
    },
    // Las ventas llegan solas desde las cajas: el monitor se refresca sin recargar.
    refetchInterval: 30_000,
    placeholderData: (prev) => prev,
  })
}

// ─── Operadores (F5) ─────────────────────────────────────────────────────────

export interface PosOperator {
  user_id: string
  name: string
  email: string
  /** `pos` = cajero que solo usa el terminal; `full` = usuario de la web que además opera una caja. */
  license_type: 'full' | 'pos'
  is_active: boolean
  roles: { id: string; name: string }[]
  stores: { id: string; code: string; name: string }[]
  has_pin: boolean
  pin_must_change: boolean
  capabilities: string[]
  max_discount_pct: string
}

/** Un rol que quien edita puede dar a un cajero (`is_web`: además abre módulos de la web). */
export interface PosAssignableRole {
  id: string
  name: string
  role_type: string
  is_web: boolean
}

export interface PosOperatorsResponse {
  operators: PosOperator[]
  pos_licenses: { contracted: number; in_use: number }
  assignable_roles: PosAssignableRole[]
}

/** Alta: `email` crea un cajero con licencia POS; `user_id` habilita a un usuario que ya está. */
export interface NewPosOperator {
  email?: string
  first_name?: string
  last_name?: string
  role_ids?: (string | number)[]
  user_id?: string
  store_ids: string[]
  pin: string
}

export interface PosOperatorChange {
  role_ids?: (string | number)[]
  store_ids?: string[]
  pin?: string
  is_active?: boolean
}

const OPERATORS = ['pos', 'operators']

export function usePosOperators() {
  return useQuery({
    queryKey: OPERATORS,
    queryFn: async () => (await apiClient.get('/pos/operators/')).data as PosOperatorsResponse,
  })
}

export function useCreatePosOperator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: NewPosOperator) => (await apiClient.post('/pos/operators/', data)).data as PosOperator,
    onSuccess: () => qc.invalidateQueries({ queryKey: OPERATORS }),
  })
}

export function useUpdatePosOperator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ userId, ...data }: PosOperatorChange & { userId: string }) =>
      (await apiClient.patch(`/pos/operators/${userId}/`, data)).data as PosOperator | '',
    onSuccess: () => qc.invalidateQueries({ queryKey: OPERATORS }),
  })
}

// ─── Cajas (F5b) ─────────────────────────────────────────────────────────────

export type CashAccountingStatus = 'pending' | 'waiting_sales' | 'blocked' | 'not_configured' | 'accounted'

export interface PosCashSession {
  id: string
  terminal_code: string
  store_code: string
  store_name: string
  status: 'open' | 'closed'
  operator: string
  closed_by: string | null
  opened_at: string
  closed_at: string | null
  opening_amount: string
  cash_sales: string
  /** Lo devuelto en efectivo por notas de crédito: se resta de lo esperado. */
  cash_refunds: string
  cash_in: string
  withdrawals: string
  expenses: string
  expected_amount: string
  declared_amount: string | null
  difference: string | null
  other_payments: Record<string, string>
  sales_received: number
  sales_reported: number | null
  accounting_status: CashAccountingStatus
  voucher_id: string | null
  voucher_number: string | null
  missing_accounts: string[]
  warnings: { code: string; message: string }[]
  movements?: {
    id: string; kind: 'cash_in' | 'withdrawal' | 'expense'; amount: string; reason: string
    operator: string; authorized_by: string | null; occurred_at: string
  }[]
  authorizations?: {
    capability: string; detail: string; operator: string; authorized_by: string
    folio: number | null; occurred_at: string
  }[]
}

export interface PosCashFilters {
  status?: string
  store?: string
  accounting_status?: string
  page?: number
}

export interface PosAccountRef { id: string; code: string; name: string }

export interface PosAccounts {
  withdrawal_account: PosAccountRef | null
  petty_expense_account: PosAccountRef | null
  stores: {
    id: string; code: string; name: string
    cash_account: PosAccountRef | null
    cash_difference_account: PosAccountRef | null
  }[]
}

const CASH = ['pos', 'cash-sessions']
const ACCOUNTS = ['pos', 'accounts']

export function usePosCashSessions(filters: PosCashFilters) {
  return useQuery({
    queryKey: [...CASH, filters],
    // Las cajas cambian mientras el local opera.
    refetchInterval: 60_000,
    queryFn: async () => (await apiClient.get('/pos/cash-sessions/', { params: filters })).data as {
      count: number; page: number; page_size: number; sessions: PosCashSession[]
      pending: Record<'not_configured' | 'blocked' | 'waiting_sales' | 'pending', number>
    },
  })
}

export function usePosCashSession(id: string | null) {
  return useQuery({
    queryKey: [...CASH, 'detail', id],
    enabled: !!id,
    queryFn: async () => (await apiClient.get(`/pos/cash-sessions/${id}/`)).data as PosCashSession,
  })
}

export function useAccountCashSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => (await apiClient.post(`/pos/cash-sessions/${id}/account/`)).data as PosCashSession,
    onSuccess: () => qc.invalidateQueries({ queryKey: CASH }),
  })
}

export function usePosAccounts(enabled = true) {
  return useQuery({
    queryKey: ACCOUNTS,
    enabled,
    queryFn: async () => (await apiClient.get('/pos/accounts/')).data as PosAccounts,
  })
}

export function useSavePosAccounts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: {
      withdrawal_account: string | null; petty_expense_account: string | null
      stores: { id: string; cash_account: string | null; cash_difference_account: string | null }[]
    }) => (await apiClient.put('/pos/accounts/', data)).data as PosAccounts,
    onSuccess: () => qc.invalidateQueries({ queryKey: ACCOUNTS }),
  })
}

// ─── Agente local: mapeos y reglas de impresión (PLAN_AGENTE_LOCAL F4.3) ─────

export interface AgentMapping {
  id: string
  document_type_code: number
  /** Vacía: el mapeo del tipo. Si no, el de la variante (`33_NPG`). */
  variant: string
  file_name: string
  /** Como lo busca el agente: `33_Template.xml` o `33_NPG_Template.xml`. */
  device_file_name: string
  sha256: string
  created_at: string
  updated_at: string
}

export interface PrintRule {
  id: string
  name: string
  /** `null`: todos los equipos de clase agente. */
  terminal: string | null
  terminal_code: string | null
  /** Vacía: todos los tipos. */
  document_types: number[]
  receiver_tax_id: string
  condition: string
  logical_printer: string
  copies: number
  cedible_copies: number
  template_family_id: string | null
  priority: number
  is_active: boolean
}

const MAPPINGS = ['pos', 'agent-mappings']
const RULES = ['pos', 'print-rules']

export function useAgentMappings() {
  return useQuery({
    queryKey: MAPPINGS,
    queryFn: async () => (await apiClient.get('/pos/agent-mappings/')).data.mappings as AgentMapping[],
  })
}

export function useUploadAgentMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, documentTypeCode, variant }: { file: File; documentTypeCode: number; variant: string }) => {
      const datos = new FormData()
      datos.append('file', file)
      datos.append('document_type_code', String(documentTypeCode))
      if (variant) datos.append('variant', variant)
      const res = await apiClient.post('/pos/agent-mappings/', datos, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      // 200: reemplazó el mapeo que ya había de ese tipo y variante; 201: es nuevo.
      return { mapping: res.data as AgentMapping, replaced: res.status === 200 }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: MAPPINGS }),
  })
}

export function useDeleteAgentMapping() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/pos/agent-mappings/${id}/`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: MAPPINGS }),
  })
}

export async function downloadAgentMapping(m: AgentMapping) {
  const res = await apiClient.get(`/pos/agent-mappings/${m.id}/`, { responseType: 'blob' })
  const url = URL.createObjectURL(res.data as Blob)
  const a = document.createElement('a')
  a.href = url
  a.download = m.device_file_name
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Instaladores de Clobi Conector y de POS Clobi (PLAN_AGENTE_LOCAL F4.4b) ──
// Los dos se publican con el mismo mecanismo y se descargan solo desde Clobi, con permiso.

export type Programa = 'conector' | 'pos'

const RUTA_DEL_PROGRAMA: Record<Programa, string> = {
  conector: '/pos/connector/',
  pos: '/pos/pos-app/',
}

const MANUAL_DEL_PROGRAMA: Record<Programa, string> = {
  conector: 'Manual-Clobi-Conector.pdf',
  pos: 'Manual-POS-Clobi.pdf',
}

export interface InstallerRelease {
  version: string
  file_name: string
  size: number
  sha256: string
  notes: string
  published_at: string
}

export function useInstallerRelease(programa: Programa) {
  return useQuery({
    queryKey: ['pos', 'installer', programa],
    queryFn: async () => (await apiClient.get(RUTA_DEL_PROGRAMA[programa])).data.release as InstallerRelease | null,
  })
}

function guardarBlob(blob: Blob, nombre: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = nombre
  a.click()
  URL.revokeObjectURL(url)
}

export async function downloadInstaller(programa: Programa, r: InstallerRelease) {
  const res = await apiClient.get(`${RUTA_DEL_PROGRAMA[programa]}download/`, { responseType: 'blob' })
  guardarBlob(res.data as Blob, r.file_name)
}

/** El manual de instalación y uso (PDF con la versión vigente en la portada). */
export async function downloadInstallerManual(programa: Programa) {
  const res = await apiClient.get(`${RUTA_DEL_PROGRAMA[programa]}manual/`, { responseType: 'blob' })
  guardarBlob(res.data as Blob, MANUAL_DEL_PROGRAMA[programa])
}

/** Una condición de una plantilla de los equipos que Clobi Conector no evalúa (§3.4 del plan). */
export interface AgentWarning {
  template_family_id: string
  template_name: string
  where: string
  condition: string
  reason: string
}

export function useAgentWarnings() {
  return useQuery({
    queryKey: ['pos', 'agent-warnings'],
    queryFn: async () => (await apiClient.get('/pos/agent-warnings/')).data.warnings as AgentWarning[],
  })
}

export function usePrintRules() {
  return useQuery({
    queryKey: RULES,
    queryFn: async () => (await apiClient.get('/pos/print-rules/')).data.rules as PrintRule[],
  })
}

export function useSavePrintRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PrintRule>) => {
      const res = data.id
        ? await apiClient.patch(`/pos/print-rules/${data.id}/`, data)
        : await apiClient.post('/pos/print-rules/', data)
      return res.data as PrintRule
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES }),
  })
}

export function useDeletePrintRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => { await apiClient.delete(`/pos/print-rules/${id}/`) },
    onSuccess: () => qc.invalidateQueries({ queryKey: RULES }),
  })
}

// ─── Reglas de las cajas: bajan a todos los terminales por GET /terminal/ ────

export interface PosCajaSettings {
  /** Solo los tipos configurados: los que faltan siguen la casilla local del equipo. */
  auto_print: Record<string, boolean>
  /** Las ocho acciones; `true` pide además el PIN de un encargado. */
  authorization_required: Record<string, boolean>
  /** `null` = sin bloqueo. */
  session_idle_timeout_minutes: number | null
}

const CAJA_SETTINGS = ['pos', 'settings']

export function usePosCajaSettings() {
  return useQuery({
    queryKey: CAJA_SETTINGS,
    queryFn: async () => (await apiClient.get('/pos/settings/')).data as PosCajaSettings,
  })
}

export function useSavePosCajaSettings() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PosCajaSettings>) =>
      (await apiClient.put('/pos/settings/', data)).data as PosCajaSettings,
    onSuccess: (data) => qc.setQueryData(CAJA_SETTINGS, data),
  })
}
